import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Card, CardContent, Grid, Chip, 
    CircularProgress, Divider, Alert, Avatar, Fade, 
    Stack, Tooltip
} from '@mui/material';
import { 
    MeetingRoomRounded, 
    LayersRounded, 
    AutoAwesomeRounded,
    CloudDoneRounded,
    SyncRounded
} from '@mui/icons-material';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../../firebase';
import Menu from '../../panel/Menu';

function RoomList() {
    const [rooms, setRooms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [error, setError] = useState(null);
    const [propertyId, setPropertyId] = useState("");

    // 1. Auth Observer
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                const snap = await getDoc(doc(db, "authroles", user.uid));
                if (snap.exists()) setPropertyId(snap.data().propertyId);
                else { setError("Profile not found."); setLoading(false); }
            } else { setError("Please log in."); setLoading(false); }
        });
        return () => unsubscribe();
    }, []);

    // 2. Data Management (Firebase + API Sync)
    useEffect(() => {
        const syncRoomData = async () => {
            if (!propertyId) return;

            try {
                // STEP A: Load from Firebase first (Fast UI)
                const localRef = doc(db, "ota_room_inventory", propertyId);
                const localSnap = await getDoc(localRef);
                
                if (localSnap.exists()) {
                    setRooms(localSnap.data().rooms);
                    setLoading(false); // UI is now populated
                }

                // STEP B: Fetch from API for updates
                setSyncing(true);
                const configSnap = await getDoc(doc(db, "channel_manager", propertyId));
                
                if (configSnap.exists()) {
                    const { hotelCode, authCode } = configSnap.data();
                    const response = await fetch('https://navajowhite-wren-495848.hostingersite.com/api/rooms', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ Authentication: { HotelCode: hotelCode, AuthCode: authCode } })
                    });
                    
                    const result = await response.json();

                    if (result.Rooms) {
                        // STEP C: Compare and Update Firebase if changed
                        const apiRoomsString = JSON.stringify(result.Rooms);
                        const localRoomsString = localSnap.exists() ? JSON.stringify(localSnap.data().rooms) : "";

                        if (apiRoomsString !== localRoomsString) {
                            await setDoc(localRef, { 
                                rooms: result.Rooms, 
                                lastUpdated: new Date().toISOString() 
                            }, { merge: true });
                            setRooms(result.Rooms);
                        }
                    }
                }
            } catch (err) {
                if (rooms.length === 0) setError("Failed to sync room data.");
                console.error(err);
            } finally {
                setLoading(false);
                setSyncing(false);
            }
        };

        syncRoomData();
    }, [propertyId]);

    return (
        <Box sx={{ display: 'flex', backgroundColor: '#F1F5F9', minHeight: '100vh' }}>
            <Menu />
            
            <Box className="content-wrapper" component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, maxWidth: '1400px', margin: '0 auto' }}>
                
                {/* Modern Header */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A' }}>
                            Room Inventory
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {syncing ? (
                                <SyncRounded sx={{ fontSize: 14, color: '#6366F1', animation: 'spin 2s linear infinite' }} />
                            ) : (
                                <CloudDoneRounded sx={{ fontSize: 14, color: '#10B981' }} />
                            )}
                            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500 }}>
                                {syncing ? "Syncing with API..." : "Up to date"}
                            </Typography>
                        </Stack>
                    </Box>
                    
                    <Chip 
                        size="small"
                        icon={<AutoAwesomeRounded style={{ fontSize: '14px', color: '#6366F1' }} />} 
                        label={`${rooms.length} Units Found`} 
                        sx={{ fontWeight: 700, bgcolor: '#FFF', border: '1px solid #E2E8F0' }} 
                    />
                </Stack>

                {loading ? (
                    <Box display="flex" justifyContent="center" mt={10}>
                        <CircularProgress thickness={5} size={40} sx={{ color: '#6366F1' }} />
                    </Box>
                ) : error && rooms.length === 0 ? (
                    <Alert severity="error">{error}</Alert>
                ) : (
                    <Grid className="row" spacing={2}>
                        {rooms.map((room, index) => (
                            <Fade in={true} key={room.RoomId}>
                                <Grid className="col-sm-3 mb-3" item xs={12} sm={6} md={4} xl={3}>
                                    <Card sx={{
                                        borderRadius: 3,
                                        border: '1px solid #E2E8F0',
                                        transition: '0.2s',
                                        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 20px -10px rgba(0,0,0,0.08)' }
                                    }}>
                                        <CardContent sx={{ p: 2 }}>
                                            <Stack direction="row" justifyContent="space-between" mb={1.5}>
                                                <Avatar sx={{ bgcolor: '#F8FAFC', color: '#475569', width: 36, height: 36, borderRadius: 2 }}>
                                                    <MeetingRoomRounded fontSize="small" />
                                                </Avatar>
                                                <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700 }}>
                                                    ID: {room.RoomId}
                                                </Typography>
                                            </Stack>

                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E293B', mb: 1.5 }}>
                                                {room.RoomName}
                                            </Typography>
                                            
                                            <Divider sx={{ mb: 1.5 }} />

                                            <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 800, textTransform: 'uppercase', mb: 1, display: 'block' }}>
                                                Active Rate Plans
                                            </Typography>

                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {room.RoomRatePlans?.map((plan) => (
                                                    <Chip
                                                        key={plan.RatePlanId}
                                                        label={plan.RatePlanName}
                                                        size="small"
                                                        sx={{ height: 22, fontSize: '0.65rem', fontWeight: 600 }}
                                                    />
                                                ))}
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Fade>
                        ))}
                    </Grid>
                )}
            </Box>
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </Box>
    );
}

export default RoomList;